import { Host, effect, listenEvent, signal, toggleClass } from "./vidstack-D9mvYVIz.js";
import { useMediaContext, useMediaState } from "./vidstack-CYE89tBD.js";
import { isHTMLElement, useResizeObserver, useTransitionActive } from "./vidstack-CgfrU1hS.js";
import { LitElement } from "./vidstack-DM8v4Khu.js";
import { DefaultAirPlayButton, DefaultAnnouncer, DefaultCaptionButton, DefaultCaptions, DefaultChapterTitle, DefaultChaptersMenu, DefaultControlsSpacer, DefaultDownloadButton, DefaultLayout, DefaultLayoutIconsLoader, DefaultPlayButton, DefaultSeekButton, DefaultSettingsMenu, DefaultTimeInvert, DefaultTimeSlider, DefaultVolumePopup, createMenuContainer, i18n, setLayoutName, useDefaultLayoutContext } from "./vidstack-0qryMFDF.js";
import { $signal, SlotManager } from "./vidstack-B1rlsgcu.js";
import { html } from "lit-html";
import { ref } from "lit-html/directives/ref.js";
//#region src/components/layouts/default/audio-layout.ts
/**
* The audio layout is our production-ready UI that's displayed when the media view type is set to
* 'audio'. It includes support for audio tracks, slider chapters, captions, live streams, and much
* more out of the box.
*
* @attr data-match - Whether this layout is being used (query match).
* @attr data-sm - The small layout is active
* @attr data-lg - The large layout is active.
* @attr data-size - The active layout size (sm or lg).
*/
var DefaultAudioLayout$1 = class extends DefaultLayout {
	static props = {
		...super.props,
		when: ({ viewType }) => viewType === "audio",
		smallWhen: ({ width }) => width < 576
	};
};
//#endregion
//#region src/elements/define/layouts/default/audio-layout.ts
function DefaultAudioLayout() {
	return [
		DefaultAnnouncer(),
		DefaultCaptions(),
		html`
      <media-controls class="vds-controls">
        <media-controls-group class="vds-controls-group">
          ${[
			DefaultSeekButton({
				backward: true,
				tooltip: "top start"
			}),
			DefaultPlayButton({ tooltip: "top" }),
			DefaultSeekButton({ tooltip: "top" }),
			DefaultAudioTitle(),
			DefaultTimeSlider(),
			DefaultTimeInvert(),
			DefaultVolumePopup({
				orientation: "vertical",
				tooltip: "top"
			}),
			DefaultCaptionButton({ tooltip: "top" }),
			DefaultDownloadButton(),
			DefaultAirPlayButton({ tooltip: "top" }),
			DefaultAudioMenus()
		]}
        </media-controls-group>
      </media-controls>
    `
	];
}
function DefaultAudioTitle() {
	return $signal(() => {
		let $ref = signal(void 0), $isTextOverflowing = signal(false), media = useMediaContext(), { title, started, currentTime, ended } = useMediaState(), { translations } = useDefaultLayoutContext(), $isTransitionActive = useTransitionActive($ref), $isContinued = () => started() || currentTime() > 0;
		const $title = () => {
			return `${i18n(translations, ended() ? "Replay" : $isContinued() ? "Continue" : "Play")}: ${title()}`;
		};
		effect(() => {
			if ($isTransitionActive() && document.activeElement === document.body) media.player.el?.focus({ preventScroll: true });
		});
		function onResize() {
			const el = $ref(), isOverflowing = !!el && !$isTransitionActive() && el.clientWidth < el.children[0].clientWidth;
			el && toggleClass(el, "vds-marquee", isOverflowing);
			$isTextOverflowing.set(isOverflowing);
		}
		function Title() {
			return html`
        <span class="vds-title-text">
          ${$signal($title)}${$signal(() => $isContinued() ? DefaultChapterTitle() : null)}
        </span>
      `;
		}
		useResizeObserver($ref, onResize);
		return title() ? html`
          <span class="vds-title" title=${$signal($title)} ${ref($ref.set)}>
            ${[Title(), $signal(() => $isTextOverflowing() && !$isTransitionActive() ? Title() : null)]}
          </span>
        ` : DefaultControlsSpacer();
	});
}
function DefaultAudioMenus() {
	const placement = "top end";
	return [DefaultChaptersMenu({
		tooltip: "top",
		placement,
		portal: true
	}), DefaultSettingsMenu({
		tooltip: "top end",
		placement,
		portal: true
	})];
}
//#endregion
//#region src/elements/define/layouts/default/audio-layout-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/layouts/default-layout}
* @example
* ```html
* <media-player>
*   <media-provider></media-provider>
*   <media-audio-layout></media-audio-layout>
* </media-player>
* ```
*/
var MediaAudioLayoutElement = class extends Host(LitElement, DefaultAudioLayout$1) {
	static tagName = "media-audio-layout";
	static attrs = { smallWhen: { converter(value) {
		return value !== "never" && !!value;
	} } };
	#media;
	#scrubbing = signal(false);
	onSetup() {
		this.forwardKeepAlive = false;
		this.#media = useMediaContext();
		this.#addLayoutClass();
		this.#setupWatchScrubbing();
	}
	onConnect() {
		this.#addLayoutClass();
		setLayoutName("audio", () => this.isMatch);
		this.#setupMenuContainer();
	}
	render() {
		return $signal(this.#render.bind(this));
	}
	#render() {
		return this.isMatch ? DefaultAudioLayout() : null;
	}
	#addLayoutClass() {
		this.classList.add("vds-audio-layout");
	}
	#setupMenuContainer() {
		const { menuPortal } = useDefaultLayoutContext();
		effect(() => {
			if (!this.isMatch) return;
			const container = createMenuContainer(this, this.menuContainer, "vds-audio-layout", () => this.isSmallLayout), roots = container ? [this, container] : [this];
			(this.$props.customIcons() ? new SlotManager(roots) : new DefaultLayoutIconsLoader(roots)).connect();
			menuPortal.set(container);
			return () => {
				container.remove();
				menuPortal.set(null);
			};
		});
	}
	#setupWatchScrubbing() {
		const { pointer } = this.#media.$state;
		effect(() => {
			if (pointer() !== "coarse") return;
			effect(this.#watchScrubbing.bind(this));
		});
	}
	#watchScrubbing() {
		if (!this.#scrubbing()) {
			listenEvent(this, "pointerdown", this.#onStartScrubbing.bind(this), { capture: true });
			return;
		}
		listenEvent(this, "pointerdown", (e) => e.stopPropagation());
		listenEvent(window, "pointerdown", this.#onStopScrubbing.bind(this));
	}
	#onStartScrubbing(event) {
		const { target } = event;
		if (!!!(isHTMLElement(target) && target.closest(".vds-time-slider"))) return;
		event.stopImmediatePropagation();
		this.setAttribute("data-scrubbing", "");
		this.#scrubbing.set(true);
	}
	#onStopScrubbing() {
		this.#scrubbing.set(false);
		this.removeAttribute("data-scrubbing");
	}
};
//#endregion
export { MediaAudioLayoutElement };
